import { toast } from 'sonner'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'

import { useLocales } from 'src/contexts'
import { EngineTypes } from 'src/constants'

import { useNavigation } from 'src/hooks/useNavigation'
import { useManageUserCars } from 'src/hooks/useManageUserCars'
import { useManageMaintenanceHistory } from 'src/hooks/useManageMaintenanceHistory'
import { getTodayString } from 'src/helpers/getTodayString'
import { formattedDateByLang } from 'src/helpers/formatDateByLang'
import { ROBOTO } from 'src/constants/fonts'

import { fuelTypeDictionary } from 'src/pages/AutoInfoForm/helpers'
import { maintenanceDictionaryType } from 'src/features/MaintenanceHistoryCard/helpers'

export const useMakePdfInfoCar = () => {
  const { translations, locale } = useLocales()
  const { goToMyCars } = useNavigation()

  const { allMaintenanceHistory } = useManageMaintenanceHistory()
  const { userCars } = useManageUserCars()

  const activeCar = userCars?.find(car => car.isActive)

  const dictionaryFuel = fuelTypeDictionary(translations.addCar)
  const dictionaryServiceType = maintenanceDictionaryType(
    translations.maintenance,
  )

  const handleDownloadPDF = (): void => {
    if (!activeCar || !allMaintenanceHistory?.length) {
      toast(translations.setting.toastWrong, {
        action: {
          label: 'Okay',
          onClick: () => toast.dismiss(),
        },
      })

      return
    }

    const doc = new jsPDF()
    doc.addFileToVFS('Roboto-Regular.ttf', ROBOTO)
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal')
    doc.setFont('Roboto')

    const margin = 15
    const pageWidth = doc.internal.pageSize.getWidth()
    let yPosition = margin

    doc.setFontSize(18)
    doc.text(
      `${translations.setting.pdf.pdfTitle}: ${activeCar.carBrand} ${activeCar.carModel} (${activeCar.year})`,
      margin,
      yPosition,
    )
    yPosition += 12

    const engineLabel = activeCar.engineType
      ? dictionaryFuel[activeCar.engineType as EngineTypes] ||
        activeCar.engineType
      : 'N/A'

    doc.setFontSize(12)
    doc.text(
      `${translations.setting.pdf.pdfVin}: ${activeCar.vin || 'N/A'}`,
      margin,
      yPosition,
    )
    yPosition += 7
    doc.text(
      `${translations.setting.pdf.pdfNumber}: ${activeCar.carNumber}`,
      margin,
      yPosition,
    )
    yPosition += 7
    doc.text(
      `${translations.setting.pdf.pdfEngine}: ${engineLabel}`,
      margin,
      yPosition,
    )
    yPosition += 7
    doc.text(
      `${translations.setting.pdf.pdfMileage}: ${activeCar.mileage} км`,
      margin,
      yPosition,
    )
    yPosition += 12

    doc.setFontSize(14)
    doc.text(`${translations.setting.pdf.pdfHistoryTitle}:`, margin, yPosition)
    yPosition += 10

    doc.setFontSize(11)
    allMaintenanceHistory.forEach(record => {
      const estimateHeight = (text: string, fontSize = 11) => {
        doc.setFontSize(fontSize)
        const lines = doc.splitTextToSize(text, pageWidth - margin * 2 - 10)

        return lines.length * 6
      }

      const dateHeight = 7
      const serviceHeight = 6
      const odoHeight = 6
      const costHeight = 6
      const separatorHeight = 11

      const addressText = `${translations.setting.pdf.pdfAddress}: ${record.address}`
      const detailsText = record.details?.trim()
        ? `${translations.setting.pdf.pdfDetails}: ${record.details}`
        : ''
      const addressHeight = estimateHeight(addressText)
      const detailsHeight = detailsText ? estimateHeight(detailsText) : 0

      const totalHeight =
        dateHeight +
        serviceHeight +
        odoHeight +
        costHeight +
        addressHeight +
        detailsHeight +
        separatorHeight

      const pageHeight = doc.internal.pageSize.getHeight()

      if (yPosition + totalHeight > pageHeight - margin) {
        doc.addPage()
        yPosition = margin
      }

      doc.setFontSize(16)
      doc.text(
        `${formattedDateByLang(record.date.seconds, locale).split(',')[0]}`,
        margin,
        yPosition,
      )
      yPosition += dateHeight

      doc.setFontSize(11)
      doc.setFont('Roboto', 'normal')
      doc.text(
        `${translations.setting.pdf.pdfService}: ${dictionaryServiceType[record.serviceType]}`,
        margin + 5,
        yPosition,
      )
      yPosition += serviceHeight

      doc.text(
        `${translations.setting.pdf.pdfOdometer}: ${record.odometer} ${record.distanceUnit}`,
        margin + 5,
        yPosition,
      )
      yPosition += odoHeight

      doc.text(
        `${translations.setting.pdf.pdfCost}: ${record.serviceCost} ${record.currency.toUpperCase()}`,
        margin + 5,
        yPosition,
      )
      yPosition += costHeight

      const addressLines = doc.splitTextToSize(
        addressText,
        pageWidth - margin * 2 - 10,
      )
      doc.text(addressLines, margin + 5, yPosition)
      yPosition += addressLines.length * 6

      if (detailsText) {
        const detailsLines = doc.splitTextToSize(
          detailsText,
          pageWidth - margin * 2 - 10,
        )
        doc.text(detailsLines, margin + 5, yPosition)
        yPosition += detailsLines.length * 6
      }

      yPosition += 3
      doc.setDrawColor(200, 200, 200)
      doc.line(margin, yPosition, pageWidth - margin, yPosition)
      yPosition += 8
    })

    const fileName = `${translations.setting.pdf.pdfFileName}_${activeCar.carBrand}_${activeCar.carModel}_${getTodayString()}.pdf`
    doc.save(fileName)
  }

  return {
    locale: translations.setting,
    handleDownloadPDF,
    activeCar: activeCar?.carNumber || '',
    goToMyCars,
  }
}
